import { api } from "../../../api/Middleware";
import {
  TRAINING_CATEGORIES_URL,
  TRAINING_PROGRESS_URL,
  TRAINING_REPORTS_URL,
  TRAINING_VIDEOS_URL,
} from "../../../api/Urls";

const CoursesService = {
  // ===============================================
  // CATEGORIAS DE CONTEÚDO GLOBAL
  // ===============================================

  getGlobalCategories: async () => {
    try {
      const response = await api.get(
        TRAINING_CATEGORIES_URL + "global_content/"
      );

      if (response.data) {
        return {
          success: true,
          data: response.data.platform_content || [],
          userPlan: response.data.user_plan,
          hasAccess: response.data.has_access,
          totalCategories: response.data.total_categories,
          totalAvailableCategories: response.data.total_available_categories,
          accessLevel: response.data.access_level,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        return {
          success: false,
          error:
            "Acesso negado. Seu plano não permite acesso ao conteúdo global.",
          errorCode: "GLOBAL_ACCESS_DENIED",
          requiredPlans: [2, 3, 4, 5, 7, 8, 9, 10, 11],
          currentPlan: error.response.data?.current_plan,
        };
      }

      if (error.response?.status === 401) {
        return {
          success: false,
          error: "Falha na autenticação. Faça login novamente.",
          errorCode: "AUTHENTICATION_REQUIRED",
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao carregar categorias",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // VÍDEOS DE CONTEÚDO GLOBAL
  // ===============================================

  getGlobalVideos: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.categoryId) {
        params.append("category_id", filters.categoryId);
      }

      // unlocked_only é ignorado para conteúdo global conforme documentação
      // if (filters.unlockedOnly) {
      //   params.append('unlocked_only', 'true');
      // }

      const url = `${TRAINING_VIDEOS_URL}global_content/${
        params.toString() ? "?" + params.toString() : ""
      }`;
      const response = await api.get(url);

      if (response.data) {
        return {
          success: true,
          data: response.data.platform_videos || [],
          userPlan: response.data.user_plan,
          hasAccess: response.data.has_access,
          totalVideos: response.data.total_videos,
          totalAvailableVideos: response.data.total_available_videos,
          filteredByCategory: response.data.filtered_by_category,
          unlockedOnlyIgnored: response.data.unlocked_only_ignored,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        const errorData = error.response.data || {};
        return {
          success: false,
          error:
            errorData.message ||
            "Acesso ao conteúdo global requer plano superior (plan_id > 1) ou permissão administrativa",
          errorCode: errorData.error_code || "ACCESS_DENIED",
          requiredPermission: errorData.required_permission,
          userIsStaff: errorData.user_is_staff,
          userPlanId: errorData.user_plan_id,
        };
      }

      if (error.response?.status === 400) {
        const errorData = error.response.data || {};
        return {
          success: false,
          error: errorData.message || "Parâmetros inválidos",
          errorCode: errorData.error_code || "INVALID_PARAMS",
          receivedValue: errorData.received_value,
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao carregar vídeos",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // DASHBOARD DE CONTEÚDO GLOBAL
  // ===============================================

  getGlobalDashboard: async () => {
    try {
      const response = await api.get(
        TRAINING_PROGRESS_URL + "global_dashboard/"
      );

      if (response.data) {
        return {
          success: true,
          data: {
            categories: response.data.categories || [],
            nextVideos: response.data.next_videos || [],
            recentActivity: response.data.recent_activity || [],
            userPlan: response.data.user_plan,
            totalCategories: response.data.total_global_categories,
            totalVideos: response.data.total_global_videos,
            overallCompletion: response.data.overall_completion,
            accessLevel: response.data.access_level,
          },
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        const errorData = error.response.data || {};
        return {
          success: false,
          error:
            errorData.message ||
            "Acesso ao conteúdo global requer plano superior (plan_id > 1) ou permissão administrativa",
          errorCode: errorData.error_code || "ACCESS_DENIED",
          requiredPermission: errorData.required_permission,
          userIsStaff: errorData.user_is_staff,
          userPlanId: errorData.user_plan_id,
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao carregar dashboard",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // ANÁLISES GLOBAIS (ADMIN APENAS)
  // ===============================================

  getGlobalAnalytics: async () => {
    try {
      const response = await api.get(
        TRAINING_REPORTS_URL + "global_analytics/"
      );

      if (response.data) {
        return {
          success: true,
          data: {
            statistics: response.data.global_statistics || {},
            popularContent: response.data.popular_global_content || [],
            contentType: response.data.content_type,
          },
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        return {
          success: false,
          error:
            "Permissão negada. Este endpoint requer acesso administrativo.",
          errorCode: "ADMIN_PERMISSION_REQUIRED",
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao carregar análises",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // UTILITÁRIOS
  // ===============================================

  checkGlobalAccess: (userPlan) => {
    return [2, 3, 4, 5, 7, 8, 9, 10, 11].includes(userPlan);
  },

  formatVideoProgress: (video) => {
    if (!video.user_progress) {
      return {
        ...video,
        progress_percent: 0,
        is_completed: false,
        status: "Não iniciado",
      };
    }

    return {
      ...video,
      progress_percent: video.user_progress.progress_percent || 0,
      is_completed: video.user_progress.completed || false,
      status: video.user_progress.status || "Não iniciado",
      last_position: video.user_progress.last_position_seconds || 0,
      started_at: video.user_progress.started_at,
      completed_at: video.user_progress.completed_at,
    };
  },

  formatCategoryProgress: (category) => {
    return {
      ...category,
      completion_percentage: category.completion_percentage || 0,
      is_completed: category.completion_percentage >= 100,
      progress_status:
        category.completion_percentage >= 100
          ? "Concluído"
          : category.completion_percentage > 0
          ? `Em progresso (${category.completion_percentage.toFixed(1)}%)`
          : "Não iniciado",
    };
  },

  // ===============================================
  // VÍDEO INDIVIDUAL
  // ===============================================

  getGlobalVideo: async (videoId) => {
    try {
      const response = await api.get(`${TRAINING_VIDEOS_URL}${videoId}/`);

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        return {
          success: false,
          error: "Acesso negado a este vídeo.",
          errorCode: "VIDEO_ACCESS_DENIED",
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          error: "Vídeo não encontrado.",
          errorCode: "VIDEO_NOT_FOUND",
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao carregar vídeo",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // STREAMING DE VÍDEO E PLAYER DATA
  // ===============================================

  getVideoStreamUrl: (videoId) => {
    // Retorna a URL de streaming do vídeo através do proxy do backend
    // TRAINING_VIDEOS_URL já inclui /api/v1/training-videos/
    const streamUrl = `${TRAINING_VIDEOS_URL}${videoId}/stream/`;

    return streamUrl;
  },

  // NOVO: Obter blob URL para vídeos internos/locais via streaming
  getVideoStreamBlob: async (videoId) => {
    try {
      const streamPath = `${TRAINING_VIDEOS_URL}${videoId}/stream/`;

      // Fazer requisição via api middleware (com autenticação)
      const response = await api.get(streamPath, {
        responseType: "blob", // Importante: receber como blob
        timeout: 30000, // 30 segundos timeout
      });

      // Criar blob URL para o ReactPlayer
      const blobUrl = URL.createObjectURL(response.data);

      return {
        success: true,
        blobUrl: blobUrl,
        blob: response.data,
      };
    } catch (error) {

      return {
        success: false,
        error:
          error.response?.data?.error ||
          error.message ||
          "Erro ao carregar vídeo",
      };
    }
  },

  getVideoUploadStatus: async (videoId) => {
    try {
      const response = await api.get(
        `${TRAINING_VIDEOS_URL}${videoId}/upload-status/`
      );

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      return {
        success: false,
        error:
          error.response?.data?.message || "Erro ao verificar status de upload",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // PROGRESSO DE TREINAMENTO (FUNCIONALIDADES FALTANTES)
  // ===============================================

  startVideo: async (videoId) => {
    try {
      const response = await api.post(TRAINING_PROGRESS_URL + "start_video/", {
        video_id: videoId,
      });

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        return {
          success: false,
          error: "Vídeo ainda não está desbloqueado",
          errorCode: "VIDEO_LOCKED",
        };
      }

      if (error.response?.status === 404) {
        return {
          success: false,
          error: "Vídeo não encontrado",
          errorCode: "VIDEO_NOT_FOUND",
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao iniciar vídeo",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  updateProgress: async (
    videoId,
    progressPercent,
    lastPositionSeconds,
    totalWatchTime
  ) => {
    try {
      const response = await api.post(
        TRAINING_PROGRESS_URL + "update_progress/",
        {
          video_id: videoId,
          progress_percent: progressPercent,
          last_position_seconds: lastPositionSeconds,
          total_watch_time: totalWatchTime,
        }
      );

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao atualizar progresso",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  completeVideo: async (videoId) => {
    try {
      const response = await api.post(
        TRAINING_PROGRESS_URL + "complete_video/",
        {
          video_id: videoId,
        }
      );

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao completar vídeo",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  getVideoPlayerData: async (videoId) => {
    try {
      const response = await api.get(
        `${TRAINING_VIDEOS_URL}${videoId}/player_data/`
      );

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      if (error.response?.status === 403) {
        return {
          success: false,
          error: "Vídeo ainda não está desbloqueado",
          errorCode: "VIDEO_LOCKED",
        };
      }

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao obter dados do player",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  checkVideoAccess: async (videoId) => {
    try {
      const response = await api.get(
        `${TRAINING_VIDEOS_URL}${videoId}/access_check/`
      );

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao verificar acesso",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  getMyProgressSummary: async () => {
    try {
      const response = await api.get(TRAINING_PROGRESS_URL + "my_summary/");

      if (response.data) {
        return {
          success: true,
          data: response.data,
        };
      }

      return {
        success: false,
        error: "Resposta inválida da API",
      };
    } catch (error) {

      return {
        success: false,
        error: error.response?.data?.message || "Erro ao obter resumo",
        errorCode: error.response?.data?.error_code || "API_ERROR",
      };
    }
  },

  // ===============================================
  // UTILITÁRIOS DE PROGRESSO
  // ===============================================

  calculateProgressPercent: (currentTime, duration) => {
    if (!duration || duration <= 0) return 0;
    return Math.min(100, Math.max(0, (currentTime / duration) * 100));
  },

  shouldCompleteVideo: (progressPercent, completionThreshold = 80.0) => {
    return progressPercent >= completionThreshold;
  },

  formatDuration: (seconds) => {
    if (!seconds || seconds <= 0) return "00:00";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }

    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  },

  // ===============================================
  // HELPER PARA URL DE VÍDEO
  // ===============================================

  getVideoUrl: (video) => {
    // Determina a URL correta do vídeo baseado no tipo
    if (!video) {
      return null;
    }

    let finalUrl = null;

    switch (video.video_type) {
      case "external":
        // Para vídeos externos (YouTube, Vimeo), usar URL externa ou video_url como fallback
        finalUrl = video.external_url || video.video_url;

        break;

      case "internal":
      case "local":
        // Para vídeos internos/locais: RETORNAR INDICADOR ESPECIAL
        // ReactPlayer precisa de blob URL, não endpoint URL
        finalUrl = `STREAMING_REQUIRED:${video.id}`;

        break;

      default:
        // Fallback para compatibilidade com dados antigos
        finalUrl = video.s3_url || video.external_url || video.video_url;

        break;
    }

    return finalUrl;
  },
};

// Exportar métodos individuais para compatibilidade
export const getGlobalCategories = CoursesService.getGlobalCategories;
export const getGlobalVideos = CoursesService.getGlobalVideos;
export const getGlobalDashboard = CoursesService.getGlobalDashboard;
export const getGlobalAnalytics = CoursesService.getGlobalAnalytics;
export const checkGlobalAccess = CoursesService.checkGlobalAccess;
export const formatVideoProgress = CoursesService.formatVideoProgress;
export const formatCategoryProgress = CoursesService.formatCategoryProgress;
export const getGlobalVideo = CoursesService.getGlobalVideo;
export const getVideoStreamUrl = CoursesService.getVideoStreamUrl;
export const getVideoStreamBlob = CoursesService.getVideoStreamBlob;
export const getVideoUploadStatus = CoursesService.getVideoUploadStatus;
export const getVideoUrl = CoursesService.getVideoUrl;

// Novos métodos de progresso
export const startVideo = CoursesService.startVideo;
export const updateProgress = CoursesService.updateProgress;
export const completeVideo = CoursesService.completeVideo;
export const getVideoPlayerData = CoursesService.getVideoPlayerData;
export const checkVideoAccess = CoursesService.checkVideoAccess;
export const getMyProgressSummary = CoursesService.getMyProgressSummary;
export const calculateProgressPercent = CoursesService.calculateProgressPercent;
export const shouldCompleteVideo = CoursesService.shouldCompleteVideo;
export const formatDuration = CoursesService.formatDuration;

export default CoursesService;
